let activeClient, activeExit, controlSocket, status;
let cachedPeerId = null;
let cachedPrivKey = null;
let statusListeners = [];
// remembered so enable()/disable() can tear down and restart on their own
let activeGlobal = null;
let activeControlHost = null;

// status codes:
// 0 - not connected to control
// 1 - failed to connect to control
// 2 - disabled
// 3 - connected to control, not connected to an exit
// 4 - connected to control, failed to connect to an exit
// 5 - connected to control+exit, idle
// 6 - connected to control+exit, active

// Server websocket protocol (snowstorm-iceberg/snowflake/conn.go):
// browser -> server: 1 Register {Offer, PeerID, Token, Exit?, ExitPinSecret?}, 4 Answer {ID, Answer},
//   5 ICE (relayed to client), 6 HungUp, 7 RegisterICE {Candidate, SdpMLineIndex},
//   8 Ready, 99 Ping
// server -> browser: 3 Offer {Msg: {ID, Offer, PeerID}}, 4 exit Answer {Msg: {ID, Answer}},
//   5 ICE {Msg: {Candidate, SdpMLineIndex}}
const CommandRegister = 1;
const CommandOffer = 3;
const CommandAnswer = 4;
const CommandICE = 5;
const CommandHungUp = 6;
const CommandRegisterICE = 7;
const CommandReady = 8;
const CommandPing = 99;

// The server only assigns another client after the browser reports Ready or
// HungUp, so a client session that never establishes must still be reaped.
const CLIENT_CONNECT_TIMEOUT_MS = 30 * 1000;

// How long registering with an exit may take, measured from the control socket
// opening to the exit leg reaching "connected". Covers offer creation, the
// broker's exit selection, the exit's answer and the ICE handshake, so it is
// deliberately generous; override with global.snowflake_exit_timeout_ms.
const EXIT_CONNECT_TIMEOUT_MS = 45 * 1000;

let log = (...args) => {
    if (typeof global === "undefined") {
        global = window;
    }
    if (global && (global.snowflake_debug || (global.location && global.location.search.indexOf("snowflake_debug") >= 0))) {
        if (typeof console !== "undefined" && console.log) {
            console.log(...args);
        }
    }
}


function toBase58(buffer) {
    const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    let num = 0n;
    for (let i = 0; i < buffer.length; i++) {
        num = (num * 256n) + BigInt(buffer[i]);
    }
    let str = '';
    while (num > 0n) {
        let rem = Number(num % 58n);
        num = num / 58n;
        str = ALPHABET[rem] + str;
    }
    for (let i = 0; i < buffer.length && buffer[i] === 0; i++) {
        str = '1' + str;
    }
    return str;
}

function fromBase58(b58) {
    const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    let num = 0n;
    let leadingOnes = 0;
    while (b58[leadingOnes] === '1') {
        leadingOnes++;
    }
    for (let i = leadingOnes; i < b58.length; i++) {
        const v = ALPHABET.indexOf(b58[i]);
        if (v < 0) return new Uint8Array(0);
        num = num * 58n + BigInt(v);
    }
    let bytes = [];
    while (num > 0n) {
        bytes.push(Number(num % 256n));
        num = num / 256n;
    }
    bytes = bytes.reverse();
    let res = new Uint8Array(leadingOnes + bytes.length);
    res.set(bytes, leadingOnes);
    return res;
}

// A libp2p Ed25519 peer ID is base58btc over the 38-byte identity multihash
// 0x00 0x24 <protobuf-wrapped 32-byte key>. The leading 0x00 is what makes the
// string start with "1"; it must not be prepended a second time.
const PEER_ID_MULTIHASH_PREFIX = [0x00, 0x24, 0x08, 0x01, 0x12, 0x20];

function peerIdFromPubKeyBytes(keyBytes) {
    const multihash = new Uint8Array(38);
    multihash.set(PEER_ID_MULTIHASH_PREFIX, 0);
    multihash.set(keyBytes, 6);
    return toBase58(multihash);
}

function peerIdMultihash(peerId) {
    let res = fromBase58(peerId);
    // Older builds emitted an extra leading "1", producing a 39-byte decode that
    // no libp2p implementation can parse. Tolerate it when reading someone
    // else's ID; getOrGeneratePeerId rewrites our own.
    if (res.length === 39 && res[0] === 0 && res[1] === 0x00 && res[2] === 0x24) {
        res = res.slice(1);
    }
    return res;
}

function getPubKeyBytesFromPeerId(peerId) {
    if (!peerId || peerId.startsWith("snowflake-")) return new Uint8Array(32);
    const res = peerIdMultihash(peerId);
    if (res.length >= 38) {
        return res.slice(6, 38);
    }
    return new Uint8Array(32);
}

function fromBase64Url(s) {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - b64.length % 4) % 4));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

async function getOrGeneratePeerId(global) {
    if (cachedPeerId) return cachedPeerId;
    let saved = global.localStorage.getItem("snowflake_peerid");
    let savedPriv = global.localStorage.getItem("snowflake_privkey");
    if (saved && savedPriv) {
        try {
            const jwk = JSON.parse(savedPriv);
            cachedPrivKey = await global.crypto.subtle.importKey(
                "jwk",
                jwk,
                "Ed25519",
                true,
                ["sign"]
            );
            // Re-derive the ID from the key we just loaded rather than trusting
            // what was stored: relays that ran an older build persisted a
            // malformed ID, and the exit drops any connection request carrying
            // one it cannot decode.
            let id = saved;
            if (jwk && jwk.x) {
                const derived = peerIdFromPubKeyBytes(fromBase64Url(jwk.x));
                if (derived !== saved) {
                    log("Rewriting stored peer ID", saved, "->", derived);
                    global.localStorage.setItem("snowflake_peerid", derived);
                }
                id = derived;
            }
            cachedPeerId = id;
            return id;
        } catch(e) { log("Error parsing privkey", e); }
    }
    try {
        const keyPair = await global.crypto.subtle.generateKey(
            "Ed25519",
            true,
            ["sign", "verify"]
        );
        const rawPubKey = await global.crypto.subtle.exportKey("raw", keyPair.publicKey);
        const exportedPriv = await global.crypto.subtle.exportKey("jwk", keyPair.privateKey);
        const keyBytes = new Uint8Array(rawPubKey);
        const peerId = peerIdFromPubKeyBytes(keyBytes);
        global.localStorage.setItem("snowflake_peerid", peerId);
        global.localStorage.setItem("snowflake_privkey", JSON.stringify(exportedPriv));
        cachedPrivKey = keyPair.privateKey;
        cachedPeerId = peerId;
        return peerId;
    } catch (e) {
        log("Error generating peer ID", e);
        cachedPeerId = "snowflake-" + Math.random().toString(36).substring(2);
        return cachedPeerId;
    }
}

const STATUS_NAMES = {
    0: "no control",
    1: "control failed",
    2: "disabled",
    3: "control, no exit",
    4: "control, exit failed",
    5: "idle",
    6: "relaying",
};

function setStatus(next) {
    if (status === next) return;
    // A healthy relay is otherwise entirely silent, which leaves no way to tell
    // "still dialling" apart from "connected and waiting for a client".
    log(`status ${status === undefined ? "-" : status} -> ${next} (${STATUS_NAMES[next] || "?"})`);
    status = next;
    for (const cb of statusListeners) {
        try { cb(next); } catch (e) { log("Status listener threw", e); }
    }
}

let exitSet = false;
let idleInterval = null;
let restartTimer = null;
let registerTimer = null;
let globalListenersAdded = false;

// Bumped every time we (re)start or tear down. A cycle's timers, promise
// callbacks and peer connection events can all land after that cycle is over --
// a rejected exit() setup and a delayed socket error are both routine -- and
// acting on them would close the control socket a *later* cycle is using and
// redial whatever controlHost the stale cycle had closed over. Every deferred
// callback therefore carries the generation it was armed in and does nothing
// once that no longer matches.
let generation = 0;

function isCurrent(gen) {
    return gen === generation;
}

function sendControl(msg) {
    if (controlSocket && controlSocket.readyState === WebSocket.OPEN) {
        controlSocket.send(JSON.stringify(msg));
    }
}

function clearRegisterTimer() {
    if (registerTimer) {
        clearTimeout(registerTimer);
        registerTimer = null;
    }
}

// Nothing in the signalling protocol tells us that a registration was rejected:
// a broker with no exit for us, or an exit that drops our connection request,
// both look exactly like an answer that has not arrived yet. Without this the
// relay parks at status 3 forever holding a control socket it will never use.
function armRegisterTimer(global, controlHost, gen) {
    clearRegisterTimer();
    let timeout = EXIT_CONNECT_TIMEOUT_MS;
    if (Number(global.snowflake_exit_timeout_ms) > 0) {
        timeout = Number(global.snowflake_exit_timeout_ms);
    }
    registerTimer = setTimeout(() => {
        registerTimer = null;
        if (!isCurrent(gen)) return;
        if (status === 5 || status === 6) return; // made it after all
        log(`No exit connection within ${timeout}ms, restarting`);
        setStatus(4);
        scheduleRestart(global, controlHost, gen);
    }, timeout);
    // A relay should never be the reason a host process stays alive.
    if (registerTimer.unref) registerTimer.unref();
}

function scheduleRestart(global, controlHost, gen) {
    if (!isCurrent(gen)) return; // a cycle we already replaced
    if (restartTimer) return;
    if (status === 2) return; // disabled, stay down
    clearRegisterTimer();
    if (controlSocket) {
        try { controlSocket.close(1000); } catch (e) { /* already closing */ }
    }
    // We have just torn the control socket down, so we are genuinely no longer
    // connected. Say so rather than relying on the socket's own close event to
    // do it: the entry point below refuses to restart while status still reads
    // as connected, and a socket that was already closed delivers no such event.
    setStatus(0);
    restartTimer = setTimeout(() => {
        restartTimer = null;
        if (!isCurrent(gen)) return;
        module.exports(global, controlHost);
    }, 1000);
    if (restartTimer.unref) restartTimer.unref();
}

// Drop the reference before closing: close() re-enters
// oniceconnectionstatechange, and that handler schedules a restart for whatever
// is still the active exit. Clearing first is what tells it this teardown was
// deliberate rather than a peer connection failing under us.
function closeActiveExit() {
    if (!activeExit) return;
    const pc = activeExit;
    activeExit = undefined;
    pc.onicecandidate = null;
    pc.oniceconnectionstatechange = null;
    try { pc.close(); } catch (e) { /* already closed */ }
}

function closeClient(pc) {
    if (!pc) return;
    if (pc.connectTimer) {
        clearTimeout(pc.connectTimer);
        pc.connectTimer = null;
    }
    pc.onicecandidate = null;
    pc.oniceconnectionstatechange = null;
    if (pc.exitPipes) {
        for (const pipe of pc.exitPipes) {
            try { pipe.close(); } catch (e) { /* already closed */ }
        }
        pc.exitPipes = [];
    }
    try { pc.close(); } catch (e) { /* already closed */ }
}

// Tear down the client session and tell the server we can take another one.
// The server only frees this browser on HungUp/Ready, so this must run exactly
// once per session no matter how the session died.
function endClientSession(pc, reason) {
    if (!pc || pc.sessionEnded) return;
    pc.sessionEnded = true;
    log("Client session ended:", reason);
    closeClient(pc);
    if (activeClient === pc) {
        activeClient = null;
        setStatus(5);
        sendControl({ "Command": CommandHungUp });
    }
}

function start(global, controlHost, gen) {
    if (!isCurrent(gen)) return;
    if (global.localStorage.getItem("snowflake_disabled")) {
        setStatus(2);
        return;
    }
    setStatus(3);
    exitSet = false;
    // Start the clock at the control socket opening rather than at registration,
    // so a hang in exit() below is covered too.
    armRegisterTimer(global, controlHost, gen);
    controlSocket.addEventListener("message", (msg) => {
        let msgJson;
        try {
            msgJson = JSON.parse(msg.data);
        } catch (e) {
            log("Ignoring unparseable control message", e);
            return;
        }
        const inner = msgJson.Msg || {};

        if (msgJson.Command == CommandOffer) { // Client connects
            if (activeClient) {
                // The server never double-books a browser; a fresh offer means
                // the previous session is dead on the server side.
                closeClient(activeClient);
                activeClient = undefined;
            }
            const offer = inner.Offer || msgJson.Offer;
            const route = inner.ID || msgJson.ID || "";
            if (!offer) {
                log("Offer message missing SDP", msgJson);
                sendControl({ "Command": CommandHungUp });
                return;
            }
            activeClient = relay(global, offer, route);
            log('Got client offer for route', route);
        } else if (msgJson.Command == CommandICE) {
            const candidate = {
                candidate: inner.Candidate !== undefined ? inner.Candidate : msgJson.Candidate,
                sdpMLineIndex: inner.SdpMLineIndex !== undefined ? inner.SdpMLineIndex : (msgJson.sdpMLineIndex || 0),
            };
            const target = activeClient || activeExit;
            if (target) {
                log("Adding ICE candidate to", activeClient ? "client" : "exit", msgJson);
                target.addIceCandidate(candidate).catch(e => {
                    log("Error adding ICE candidate", e, msgJson);
                });
            }
        } else if (msgJson.Command == CommandAnswer && !exitSet) { // Exit Answer
            log("Exit Answer", msgJson);
            exitSet = true;
            activeExit.routeUuid = inner.ID || inner.Route || "";
            activeExit.setRemoteDescription({ type: "answer", sdp: inner.Answer || msgJson.Answer })
                .catch(e => {
                    log("Error setting exit answer", e);
                    scheduleRestart(global, controlHost, gen);
                });
        }
    });
    if (global.addEventListener && !globalListenersAdded) {
        globalListenersAdded = true;
        global.addEventListener("beforeunload", () => {
            controlSocket.close(1000);
        });
        global.addEventListener("message", (event) => {
            if (event.data == "disable") {
                global.localStorage.setItem("snowflake_disabled", "true")
                window.location.reload();
            } else if (event.data == "enable") {
                global.localStorage.removeItem("snowflake_disabled")
                window.location.reload();
            } else if (event.data == "poll") {
                event.source.postMessage({
                    "status": status,
                }, event.origin);
            }
        });
    }

    // signal ready / registry
    exit(global, controlHost, gen).then(async (pc) => {
        if (!isCurrent(gen)) return; // torn down while the offer was being built
        const myPeerId = await getOrGeneratePeerId(global);
        if (!isCurrent(gen)) return;

        const token = global.localStorage.getItem("access") || "";
        const msg = {
            "Offer": pc.localDescription.sdp,
            "Token": token,
            "PeerID": myPeerId,
        };
        // Test/staging deployments can pin a snowflake to a chosen exit instead
        // of taking the broker's hashring placement. The broker ignores this
        // unless it was started with a matching SNOWFLAKE_EXIT_PIN_SECRET.
        if (global.snowflake_exit && global.snowflake_exit_pin_secret) {
            msg.Exit = global.snowflake_exit;
            msg.ExitPinSecret = global.snowflake_exit_pin_secret;
            log("Requesting pinned exit", global.snowflake_exit);
        }
        sendControl({
            "Command": CommandRegister,
            "Msg": msg,
        });
        activeExit = pc;
    }).catch((e) => {
        // A teardown mid-setup rejects this (the peer connection closes under
        // setLocalDescription); that is not a failure worth reacting to.
        if (!isCurrent(gen)) return;
        log("Error setting up exit connection", e);
        setStatus(4);
        scheduleRestart(global, controlHost, gen);
    });
}

function relay(global, offer, route) {
    // todo: different stun based on client-observed region/locale.
    const clientPC = new global.RTCPeerConnection({
        "iceServers": [
            { "urls": "stun:stun1.l.google.com:19302" },
            { "urls": "stun:stun.nextcloud.com:443" }
        ],
    });
    clientPC.clientRoute = route;
    clientPC.exitPipes = [];
    clientPC.sessionEnded = false;
    clientPC.connectTimer = setTimeout(() => {
        endClientSession(clientPC, "connect timeout");
    }, CLIENT_CONNECT_TIMEOUT_MS);

    clientPC.onicecandidate = (event) => {
        log("client ice candidate", event.candidate);
        if (event.candidate) {
            sendControl({
                "Command": CommandICE,
                "Msg": {
                    "Candidate": event.candidate.candidate,
                    "SdpMLineIndex": event.candidate.sdpMLineIndex || 0,
                    "SdpMid": event.candidate.sdpMid,
                    "UsernameFragment": event.candidate.usernameFragment,
                },
            });
        }
    };
    clientPC.oniceconnectionstatechange = () => {
        const st = clientPC.iceConnectionState;
        if (st === "connected" || st === "completed") {
            log("client ice connected");
            if (clientPC.connectTimer) {
                clearTimeout(clientPC.connectTimer);
                clientPC.connectTimer = null;
            }
        } else if (st === "disconnected" || st === "failed" || st === "closed") {
            endClientSession(clientPC, "client ice " + st);
        }
    };

    clientPC.addEventListener('datachannel', event => {
        log("data channel opened / relaying began")
        setStatus(6);
        const dataChannel = event.channel;
        dataChannel.binaryType = 'arraybuffer';

        if (!activeExit || !exitSet) {
            log("No exit available for relaying, hanging up");
            endClientSession(clientPC, "no exit");
            return;
        }

        // Mirror the client's channel label (which encodes its route) onto the
        // exit leg so the exit can associate the stream with the same route.
        let exitPipe;
        try {
            exitPipe = activeExit.createDataChannel(dataChannel.label);
        } catch (e) {
            log("Error creating exit data channel", e);
            endClientSession(clientPC, "exit channel failure");
            return;
        }
        exitPipe.binaryType = 'arraybuffer';
        clientPC.exitPipes.push(exitPipe);

        // Queue client bytes until the exit leg is open, then pipe directly.
        let pending = [];
        dataChannel.addEventListener('message', (e) => {
            if (pending === null) {
                if (exitPipe.readyState === 'open') exitPipe.send(e.data);
            } else {
                pending.push(e.data);
            }
        });
        exitPipe.addEventListener('message', (e) => {
            if (dataChannel.readyState === 'open') dataChannel.send(e.data);
        });
        const flush = () => {
            while (pending && pending.length > 0) {
                exitPipe.send(pending.shift());
            }
            pending = null;
        };
        if (exitPipe.readyState === 'open') {
            flush();
        } else {
            exitPipe.addEventListener('open', flush);
        }

        dataChannel.addEventListener('close', () => {
            log("Data channel closed by client");
            try { exitPipe.close(); } catch (e) { /* already closed */ }
            endClientSession(clientPC, "client channel closed");
        });
        exitPipe.addEventListener('close', () => {
            log("Data channel closed by exit");
            try { dataChannel.close(); } catch (e) { /* already closed */ }
            endClientSession(clientPC, "exit channel closed");
        });
    });

    clientPC.setRemoteDescription({ type: "offer", sdp: offer }).then(() => {
        return clientPC.createAnswer();
    }).then(async (answer) => {
        await clientPC.setLocalDescription(answer);
        sendControl({
            "Command": CommandAnswer, "Msg": {
                "ID": route,
                "Answer": answer.sdp,
                "Type": answer.type,
            }
        });
    }).catch((e) => {
        log("Error answering client offer", e);
        endClientSession(clientPC, "answer failure");
    });
    return clientPC
};

async function exit(global, controlHost, gen) {
    const exitPC = new global.RTCPeerConnection({
        "iceServers": [
            { "urls": "stun:stun1.l.google.com:19302" },
            { "urls": "stun:stun.telnyx.com:3478" },
            { "urls": "stun:stun.hot-chilli.net:3478" },
            { "urls": "stun:stun.fitauto.ru:3478" },
            { "urls": "stun:stun.m-online.net:3478" }
        ],
    });
    // Claim it as the active exit at construction, not once registration has
    // been sent: a restart in between would otherwise drop the only reference
    // to a live peer connection, leaking it for the lifetime of the process.
    // It also closes the window where an early answer finds activeExit unset.
    activeExit = exitPC;
    exitPC.onicecandidate = (event) => {
        if (event.candidate) {
            sendControl({
                "Command": CommandRegisterICE, "Msg": {
                    "Candidate": event.candidate.candidate,
                    "SdpMLineIndex": event.candidate.sdpMLineIndex || 0,
                }
            });
        }
    };
    exitPC.oniceconnectionstatechange = (event) => {
        if (!isCurrent(gen)) return;
        if (exitPC.iceConnectionState === "connected") {
            clearRegisterTimer();
            if (status != 5) {
                setStatus(5);
                log("Exit connected", event);
                activeExit = exitPC;
                sendControl({ "Command": CommandReady });
            }
        } else if (exitPC.iceConnectionState === "disconnected" || exitPC.iceConnectionState === "failed") {
            log("Exit disconnected", event);
            if (activeExit === exitPC) {
                setStatus(4);
                scheduleRestart(global, controlHost, gen);
            }
        }
    };
    let authDC = exitPC.createDataChannel("snowflake");
    authDC.binaryType = 'arraybuffer';
    authDC.onopen = async () => {
        let authPayload = new Uint8Array(100);

        // [version] 4 bytes, '1' little endian
        authPayload[0] = 1;

        // [transport_auth] 32 bytes SHA-256
        let sdp = exitPC.remoteDescription ? exitPC.remoteDescription.sdp : "";
        let match = sdp.match(/a=fingerprint:sha-256\s+([A-Fa-f0-9:]+)/);
        if (match) {
            let hex = match[1].replace(/:/g, '');
            for (let i = 0; i < 32 && i*2 < hex.length; i++) {
                authPayload[4 + i] = parseInt(hex.substr(i*2, 2), 16);
            }
        }

        // [client public key] 32 bytes
        let pubKeyBytes = getPubKeyBytesFromPeerId(cachedPeerId);
        authPayload.set(pubKeyBytes, 36);

        // [enc:recipient peer id] 32 bytes
        let exitPubKeyBytes = getPubKeyBytesFromPeerId(exitPC.exitPeerId || "");
        authPayload.set(exitPubKeyBytes, 68);

        let authHeader = new Uint8Array(164); // 100 payload + 64 sig
        authHeader.set(authPayload, 0);

        if (cachedPrivKey) {
            try {
                let sigBuf = await global.crypto.subtle.sign(
                    "Ed25519",
                    cachedPrivKey,
                    authPayload
                );
                authHeader.set(new Uint8Array(sigBuf), 100);
            } catch (e) {
                log("Error signing auth header", e);
            }
        }

        authDC.send(authHeader);
    };
    let offer = await exitPC.createOffer({ iceRestart: true, offerToReceiveAudio: false, offerToReceiveVideo: false, voiceActivityDetection: false });
    await exitPC.setLocalDescription(offer);
    return exitPC
};

module.exports = async (global, controlHost) => {
    // if we're connected, lets not restart
    if (status === 3 || status === 5 || status === 6) {
        log("Already connected, not restarting");
        return
    }
    activeGlobal = global;
    activeControlHost = controlHost;
    if (global.logger) {
        log = global.logger;
    }
    // Don't open a control socket at all for someone who has opted out; start()
    // also checks, but only after the socket is already up.
    if (global.localStorage.getItem("snowflake_disabled")) {
        setStatus(2);
        return;
    }
    setStatus(0);

    // reset any state left over from a previous control connection
    const gen = ++generation;
    exitSet = false;
    clearRegisterTimer();
    if (activeClient) {
        closeClient(activeClient);
        activeClient = undefined;
    }
    closeActiveExit();

    await getOrGeneratePeerId(global);

    // Sockets we've replaced or closed can still deliver error/close events, so
    // every handler ignores anything that isn't the socket we're currently on.
    const socket = new WebSocket(controlHost + "/snowflake", "snowflake")
    controlSocket = socket;
    socket.addEventListener("open", start.bind(this, global, controlHost, gen));
    socket.addEventListener("error", (e) => {
        if (controlSocket !== socket || status === 2) return;
        setStatus(1);
        log("Control socket error", e);
        scheduleRestart(global, controlHost, gen);
    });
    socket.addEventListener("close", () => {
        if (controlSocket !== socket || status === 2) return; // disabled, stay down
        log("Control socket closed");
        setStatus(0);
        scheduleRestart(global, controlHost, gen);
    });

    if (idleInterval) {
        clearInterval(idleInterval);
    }
    idleInterval = setInterval(() => {
        if (controlSocket && controlSocket.readyState === WebSocket.OPEN &&
            status === 5) {
            sendControl({ "Command": CommandPing });
        }
    }, 10 * 1000);
}

module.exports.getStatus = () => status;

// Subscribe to status changes. Returns an unsubscribe function.
module.exports.onStatus = (cb) => {
    statusListeners.push(cb);
    return () => {
        statusListeners = statusListeners.filter((l) => l !== cb);
    };
};

// Whether the user has opted in. This is the persisted preference, not the
// live connection state — use getStatus() for that.
module.exports.isEnabled = () => {
    if (!activeGlobal) return false;
    return !activeGlobal.localStorage.getItem("snowflake_disabled");
};

// Stop relaying and remember the choice across page loads.
module.exports.disable = () => {
    if (!activeGlobal) return;
    activeGlobal.localStorage.setItem("snowflake_disabled", "true");
    generation++; // invalidate anything the running cycle still has in flight
    if (idleInterval) {
        clearInterval(idleInterval);
        idleInterval = null;
    }
    if (restartTimer) {
        clearTimeout(restartTimer);
        restartTimer = null;
    }
    clearRegisterTimer();
    if (activeClient) {
        closeClient(activeClient);
        activeClient = undefined;
    }
    closeActiveExit();
    exitSet = false;
    // set before closing the socket so its close handler doesn't restart us
    setStatus(2);
    if (controlSocket) {
        try { controlSocket.close(1000); } catch (e) { /* already closing */ }
        controlSocket = undefined;
    }
};

// Clear the remembered opt-out and reconnect.
module.exports.enable = () => {
    if (!activeGlobal) return;
    activeGlobal.localStorage.removeItem("snowflake_disabled");
    if (status === 2) setStatus(0); // clear the disabled latch before restarting
    module.exports(activeGlobal, activeControlHost);
};

// exposed for tests
module.exports._internals = {
    reset() {
        setStatus(2); // block scheduleRestart from reviving us during teardown
        generation++; // and invalidate every callback the cycle still has pending
        if (idleInterval) clearInterval(idleInterval);
        idleInterval = null;
        if (restartTimer) clearTimeout(restartTimer);
        restartTimer = null;
        clearRegisterTimer();
        if (activeClient) closeClient(activeClient);
        activeClient = undefined;
        closeActiveExit();
        if (controlSocket) {
            try { controlSocket.close(1000); } catch (e) { /* already closed */ }
        }
        controlSocket = undefined;
        // leave status at 2 so late close/error events don't schedule restarts;
        // a fresh module.exports() call resets it to 0.
        exitSet = false;
        globalListenersAdded = false;
        statusListeners = [];
        activeGlobal = null;
        activeControlHost = null;
    },
    getStatus() { return status; },
};
